import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import actions from "@Src/redux/rootActions";
import { selectors } from "@Src/redux/reducers";
import {
  getOfferModalData,
  getModalTriggerElement,
} from "@Src/redux/reducers/offerModal/offerModalSelectors";
import { handleExternalRedirect, handleInterstitialRedirect } from "@Src/utils/redirectUtility";

const modalActions = actions.offerModal;

/**
 * Hook for managing OfferModal state through Redux
 * Provides a clean API for opening/closing modals and managing focus
 */
export const useOfferModal = () => {
  const dispatch = useDispatch();
  const program = useSelector(selectors.getProgram);
  const language = useSelector(selectors.getLanguageSelector);
  const modalData = useSelector(getOfferModalData);
  const triggerElement = useSelector(getModalTriggerElement);
  const focusTimeoutRef = useRef(null);

  const navigate = useNavigate();

  /**
   * Opens the OfferModal with the specified store and options
   * @param {string} storeKey - The store key to display in the modal
   * @param {Object} props - Additional props to pass to the modal
   * @param {React.RefObject} triggerRef - Reference to the element that triggered the modal
   */
  const openModal = useCallback(
    (storeKey, props = {}, triggerRef = null) => {
      if (!storeKey) {
        console.warn("useOfferModal.openModal: storeKey is required");
        return;
      }

      // Generate or use existing ID for trigger element
      let triggerElement = null;
      if (triggerRef?.current) {
        triggerElement = triggerRef.current;
      }

      dispatch(
        modalActions.openModal({
          storeKey,
          props,
          triggerElement,
        }),
      );
    },
    [dispatch],
  );

  /**
   * Closes the modal and restores focus to trigger element
   */
  const closeModal = useCallback(() => {
    dispatch(modalActions.closeModal());
  }, [dispatch]);

  /**
   * Updates modal props without closing and reopening
   * @param {Object} newProps - Props to merge with existing modal props
   */
  const updateProps = useCallback(
    (newProps) => {
      dispatch(modalActions.updateModalProps(newProps));
    },
    [dispatch],
  );

  /**
   * Sets modal loading state
   * @param {boolean} isLoading - Loading state
   */
  const setLoading = useCallback(
    (isLoading) => {
      dispatch(modalActions.setModalLoading(isLoading));
    },
    [dispatch],
  );

  /**
   * Sets modal error state
   * @param {string|null} error - Error message or null to clear
   */
  const setError = useCallback(
    (error) => {
      dispatch(modalActions.setModalError(error));
    },
    [dispatch],
  );

  /**
   * Handle focus restoration when modal closes
   */
  useEffect(() => {
    // Clear any existing timeout
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }

    if (!modalData.isOpen && triggerElement) {
      // Small delay to ensure modal DOM is cleaned up before focus restoration
      focusTimeoutRef.current = setTimeout(() => {
        if (triggerElement) {
          triggerElement.focus();
        }

        // Clear the trigger element ID from state
        dispatch(modalActions.clearTriggerElement());
      }, 50);
    }

    // Cleanup timeout on unmount
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, [modalData.isOpen, triggerElement, dispatch]);

  /**
   * Handle click item - handle external/internal URLs or open offer modal
   * @param {Object} store - Store object with store_key, external_url, etc.
   * @param {React.RefObject} triggerRef - Reference to trigger element
   */
  const handleClickItem = useCallback(
    (store, triggerRef = null) => {
      if (!store) {
        console.warn("useOfferModal.handleClickItem: store object is required");
        return;
      }

      // Normalize store_key (handle both store_key and storeKey properties)
      const storeKey = store.store_key || store.storeKey;

      // Handle external URLs
      if (store.external_url) {
        const hasStoreInfo = store.store_name && store.logo_url && storeKey;
        const { name, program_color, program_logo } = program || {};
        if (hasStoreInfo) {
          const storeData = {
            store_name: store.store_name,
            store_logo_url: store.logo_url,
            store_url: store.external_url,
            program_name: name,
            program_color: program_color,
            program_logo: program_logo,
            language: language,
          };
          handleInterstitialRedirect(storeData);
        } else {
          handleExternalRedirect(store.external_url);
        }
        return;
      }

      // Handle internal URLs
      if (store.internal_url) {
        navigate(store.internal_url);
        return;
      }

      if (!storeKey) {
        console.warn(
          "useOfferModal.handleClickItem: store must have store_key or storeKey property",
        );
        return;
      }

      // Regular modal opening
      openModal(storeKey, {}, triggerRef);
    },
    [program, language, openModal, navigate],
  );

  return {
    // State
    ...modalData,

    // Actions
    openModal,
    closeModal,
    updateProps,
    setLoading,
    setError,

    // Enhanced handlers
    handleClickItem,
  };
};
